// Presentation helpers shared by the header bell and the /notifications page.
// Mirrors app/models/notification.py severity + event vocabularies.

import { Icons } from '@/components/ui/icons'
import type { NotificationSeverity } from '@/types/notification'

interface SeverityMeta {
  icon: React.ElementType
  /** Screen-reader text so severity is never conveyed by color alone (Rule 14 §5). */
  label: string
  color: string
  bg: string
  badge: string
}

export const SEVERITY_META: Record<NotificationSeverity, SeverityMeta> = {
  info: {
    icon: Icons.info,
    label: 'Information',
    color: 'text-brand-cornflower',
    bg: 'bg-brand-cornflower/10',
    badge: 'bg-brand-cornflower/10 text-brand-cornflower border border-brand-cornflower/30',
  },
  warning: {
    icon: Icons.alertTriangle,
    label: 'Needs attention',
    color: 'text-warning',
    bg: 'bg-warning/10',
    badge: 'bg-warning/10 text-warning-foreground border border-warning/30',
  },
  error: {
    icon: Icons.alertCircle,
    label: 'Error',
    color: 'text-destructive',
    bg: 'bg-destructive/10',
    badge: 'bg-destructive/10 text-destructive border border-destructive/30',
  },
}

export const SEVERITY_ORDER: NotificationSeverity[] = ['error', 'warning', 'info']

export function severityMeta(severity: string): SeverityMeta {
  return SEVERITY_META[severity as NotificationSeverity] ?? SEVERITY_META.info
}

export function formatRelativeTime(iso: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''

  const diffMins = Math.floor((Date.now() - then) / 60000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(iso).toLocaleDateString()
}
